import { Box, Paper, Typography } from "@mui/material";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { Section, SectionHeading } from "../section/section";

const BENEFITS = [
  { icon: SpeedRoundedIcon, stat: "3x", title: "Faster Inventory Management", desc: "Cut manual tracking time with automated inventory updates.", color: "#4f6bed" },
  { icon: VisibilityRoundedIcon, stat: "100%", title: "Better Manufacturing Visibility", desc: "See every production batch and its status in real time.", color: "#16b8a6" },
  { icon: TrendingUpRoundedIcon, stat: "+24%", title: "Improved Revenue Tracking", desc: "Categorize and monitor revenue with date-wise records.", color: "#8b5cf6" },
  { icon: HubRoundedIcon, stat: "1", title: "Centralized Operations", desc: "Bring products, managers, and revenue into one place.", color: "#f59e0b" },
  { icon: BoltRoundedIcon, stat: "Live", title: "Real-Time Insights", desc: "Instant dashboards that update as your business moves.", color: "#06b6d4" },
  { icon: InsightsRoundedIcon, stat: "360°", title: "Business Growth Monitoring", desc: "Track KPIs and growth trends across every workflow.", color: "#10b981" },
];

function Benefits() {

  return (
    <Section
      id="benefits"
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 10, md: 16 },

        background: `
          radial-gradient(circle at top left, rgba(59,130,246,.15), transparent 30%),
          radial-gradient(circle at bottom right, rgba(14,165,233,.12), transparent 35%),
          linear-gradient(180deg,#020617 0%,#0f172a 45%,#111827 100%)
        `,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -150,
          left: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "#2563eb",
          filter: "blur(140px)",
          opacity: 0.12,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          bottom: -180,
          right: -180,
          width: 450,
          height: 450,
          borderRadius: "50%",
          background: "#06b6d4",
          filter: "blur(160px)",
          opacity: 0.1,
        }}
      />
      <SectionHeading
        eyebrow="Why FlowSync"
        title="Built for measurable business impact"
        subtitle="Operational gains you can feel from day one."
        sx={{
          "& .eyebrow": {
            color: "#60a5fa",
          },
          "& .title": {
            color: "#f8fafc",
          },
          "& .subtitle": {
            color: "rgba(248,250,252,.72)",
          },
        }}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(3, 1fr)",
          },
          gap: 2.5,
        }}
      >
        {BENEFITS.map((b) => {
          const Icon = b.icon;

          return (
            <Paper
              key={b.title}
              elevation={0}
              sx={{
                position: "relative",
                overflow: "hidden",
                p: 3,
                borderRadius: "24px",
                background:"linear-gradient(180deg,#111827 0%,#0f172a 100%)",
                border: "1px solid rgba(59,130,246,.12)",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                transition: "all .35s ease",
                boxShadow: `
                  0 10px 30px rgba(0,0,0,.35),
                  inset 0 1px 0 rgba(255,255,255,.03)
                `,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: 120,
                  height: 120,
                  top: -40,
                  right: -40,
                  borderRadius: "50%",
                  background: b.color,
                  opacity: 0.08,
                  filter: "blur(50px)",
                },
                "&:hover": {
                  transform: "translateY(-8px)",
                  borderColor: b.color,
                  boxShadow: `
                    0 20px 40px rgba(0,0,0,.45),
                    0 0 25px ${b.color}25
                  `,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "16px",
                    display: "grid",
                    placeItems: "center",
                    background: `${b.color}22`,
                    border: `1px solid ${b.color}33`,
                  }}
                >
                  <Icon
                    sx={{
                      color: b.color,
                      fontSize: 24,
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "1.8rem",
                    color: b.color,
                    letterSpacing: "-0.04em",
                  }}
                >
                  {b.stat}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  color: "#f8fafc",
                  mb: 1,
                }}
              >
                {b.title}
              </Typography>

              <Typography
                sx={{
                  color: "rgba(248,250,252,.7)",
                  fontSize: ".95rem",
                  lineHeight: 1.7,
                }}
              >
                {b.desc}
              </Typography>
            </Paper>
          );
        })}
      </Box>
    </Section>
  );
}

export default Benefits;